import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  TrendingUp,
  BarChart,
  Campaign,
  Insights,
  Settings,
  RestaurantMenu,
  Search
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import apiClient from '../../../api/client';

// Import AiDialog component
import AiDialog from './dialogs/AiDialog.jsx';

const AiInsight = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const MotionDiv = motion.div;
  
  // Conversation topics with icons - now using translation keys
  const CONVERSATION_TOPICS = [
    { 
      value: 'market_trends', 
      label: t('ai.topic.marketTrends'),
      icon: <TrendingUp /> 
    },
    { 
      value: 'sales_performance', 
      label: t('ai.topic.salesPerformance'),
      icon: <BarChart /> 
    },
    { 
      value: 'marketing_strategies', 
      label: t('ai.topic.marketingStrategies'),
      icon: <Campaign /> 
    },
    { 
      value: 'business_improvement', 
      label: t('ai.topic.businessImprovement'),
      icon: <Insights /> 
    },
    { 
      value: 'operations_optimization', 
      label: t('ai.topic.operationsOptimization'),
      icon: <Settings /> 
    },
    { 
      value: 'menu_development', 
      label: t('ai.topic.menuDevelopment'),
      icon: <RestaurantMenu /> 
    }
  ];
  
  // Form state
  const [query, setQuery] = useState({
    topic: '',
    title: '',
    description: '',
    priority: 'medium'
  });
  
  // AI response states
  const [openAiDialog, setOpenAiDialog] = useState(false);
  const [currentAiResponse, setCurrentAiResponse] = useState(null);
  const [storedInsights, setStoredInsights] = useState([]);
  const [isGeneratingInsight, setIsGeneratingInsight] = useState(false);
  const [isLoadingStoredInsights, setIsLoadingStoredInsights] = useState(true);
  
  // Handle query field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuery(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Generate an AI insight based on the query
  const generateAiInsight = async (isRandom = false) => {
    setIsGeneratingInsight(true);
    
    try {
      console.log('🤖 Generating AI insight...', { isRandom, query });
      
      const endpoint = isRandom 
        ? '/api/v1/ai/random-insight'
        : '/api/v1/ai/generate-hawker-insight';
        
      const method = isRandom ? 'get' : 'post';
      
      const requestData = {
        ...(!isRandom && { query }),
        language: i18n.language,
        locale: i18n.language
      };
      
      const response = await apiClient({
        method,
        url: endpoint,
        data: requestData,
        params: isRandom ? { 
          language: i18n.language,
          locale: i18n.language 
        } : undefined,
        timeout: 60000 // ✅ Increase timeout to 60 seconds
      });
      
      console.log('✅ AI insight generated successfully');
      
      setCurrentAiResponse(response.data.insight);
      setOpenAiDialog(true);
      
      if (!isRandom) {
        setQuery({
          topic: '',
          title: '',
          description: '',
          priority: 'medium'
        });
      }
    } catch (error) {
      console.error('❌ Error generating AI insight:', error);
      
      // ✅ ENHANCED: Better error messages
      let errorMessage = t('ai.errorGenerating');
      
      if (error.response?.data?.error === 'MISSING_API_KEY') {
        errorMessage = 'AI service is not configured. Please contact support.';
      } else if (error.response?.data?.error === 'QUOTA_EXCEEDED') {
        errorMessage = 'AI service quota exceeded. Please try again later.';
      } else if (error.response?.data?.error === 'RATE_LIMIT') {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = 'Request timed out. The AI is taking longer than expected. Please try again.';
      }
      
      alert(errorMessage);
    } finally {
      setIsGeneratingInsight(false);
    }
  };
  
  // Submit the query to the API
  const handleQuerySubmit = async (e) => {
    e.preventDefault();
    
    if (!query.topic || !query.title) {
      alert(t('ai.selectTopicAndTitle'));
      return;
    }
    
    generateAiInsight(false);
  };
  
  // Fetch stored insights from the API
  const fetchStoredInsights = useCallback(async () => {
    setIsLoadingStoredInsights(true);
    
    try {
      const response = await apiClient.get('/api/v1/ai/stored-insights', {
        params: {
          language: i18n.language // Include language for localized insights
        }
      });
      setStoredInsights(response.data);
    } catch (error) {
      console.error('Error fetching stored insights:', error);
      setStoredInsights([]);
    } finally {
      setIsLoadingStoredInsights(false);
    }
  }, [i18n.language]);
  
  // Navigate back to dashboard
  const handleBack = () => {
    navigate('/dashboard');
  };
  
  // Save the current AI response
  const handleSaveInsight = async () => {
    try {
      // Make an explicit API call to save the insight
      await apiClient.post('/api/v1/ai/save-insight', {
        insight: currentAiResponse,
        topic: query.topic,
        title: query.title,
        priority: query.priority,
        language: i18n.language // Save with language information
      });
      
      setOpenAiDialog(false);
      
      // Refresh the list of stored insights
      fetchStoredInsights();
    } catch (error) {
      console.error('Error saving insight:', error);
      alert(t('ai.errorSaving'));
    }
  };
  
  // Lifecycle hook to fetch stored insights on component mount
  useEffect(() => {
    fetchStoredInsights();
  }, [fetchStoredInsights]); // Re-fetch when language changes

  return (
    <Box>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {t('ai.title')}
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBack}
          >
            {t('ai.backToDashboard')}
          </Button>
        </Box>
      </MotionDiv>
      
      <form onSubmit={handleQuerySubmit}>
        <Grid container spacing={3}>
          {/* Query Form */}
          <Grid item xs={12} md={7}>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('ai.askQuestion')}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>{t('ai.businessTopic')}</InputLabel>
                        <Select
                          name="topic"
                          value={query.topic}
                          onChange={handleChange}
                          label={t('ai.businessTopic')}
                        >
                          {CONVERSATION_TOPICS.map(topic => (
                            <MenuItem key={topic.value} value={topic.value}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ mr: 1 }}>{topic.icon}</Box>
                                {topic.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>{t('ai.priority')}</InputLabel>
                        <Select
                          name="priority"
                          value={query.priority}
                          onChange={handleChange}
                          label={t('ai.priority')}
                        >
                          <MenuItem value="low">{t('ai.priorityLow')}</MenuItem>
                          <MenuItem value="medium">{t('ai.priorityMedium')}</MenuItem>
                          <MenuItem value="high">{t('ai.priorityHigh')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        label={t('ai.questionTitle')}
                        name="title"
                        value={query.title}
                        onChange={handleChange}
                        fullWidth
                        required
                        placeholder={t('ai.questionPlaceholder')}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        label={t('ai.describeChallenge')}
                        name="description"
                        value={query.description}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        placeholder={t('ai.challengePlaceholder')}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isGeneratingInsight}
                          sx={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                            }
                          }}
                        >
                          {isGeneratingInsight ? (
                            <>
                              <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                              {t('ai.generating')}
                            </>
                          ) : t('ai.getBotLahAdvice')}
                        </Button>
                        
                        <Button
                          variant="outlined"
                          onClick={() => generateAiInsight(true)}
                          disabled={isGeneratingInsight}
                        >
                          {t('ai.generateRandomInsight')}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </MotionDiv>
            
            {/* AI Features and Benefits */}
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card sx={{ mt: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('ai.howAiHelps')}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                          <TrendingUp sx={{ mr: 1, color: theme.palette.primary.main }} />
                          {t('ai.marketAnalysis')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('ai.marketAnalysisDesc')}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                          <BarChart sx={{ mr: 1, color: theme.palette.primary.main }} />
                          {t('ai.salesOptimization')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('ai.salesOptimizationDesc')}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                          <Settings sx={{ mr: 1, color: theme.palette.primary.main }} />
                          {t('ai.costOptimization')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('ai.costOptimizationDesc')}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                          <Campaign sx={{ mr: 1, color: theme.palette.primary.main }} />
                          {t('ai.marketingStrategies')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('ai.marketingStrategiesDesc')}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </MotionDiv>
          </Grid>
          
          {/* Stored Insights */}
          <Grid item xs={12} md={5}>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('ai.savedInsights')}
                    </Typography>
                    
                    <Button
                      variant="text"
                      size="small"
                      onClick={fetchStoredInsights}
                    >
                      {t('ai.refresh')}
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  
                  {isLoadingStoredInsights ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : storedInsights.length === 0 ? (
                    <Box sx={{ textAlign: 'center', my: 4 }}>
                      <Search sx={{ fontSize: 48, color: theme.palette.primary.main, opacity: 0.5, mb: 2 }} />
                      <Typography color="text.secondary">
                        {t('ai.noSavedInsights')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('ai.askQuestionToStart')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: '500px', overflowY: 'auto', pr: 1 }}>
                      {storedInsights.map((insight) => (
                        <Accordion 
                          key={insight.id} 
                          sx={{ 
                            mb: 2,
                            boxShadow: 'none',
                            border: '1px solid #eee',
                            borderRadius: '12px !important',
                            '&:before': {
                              display: 'none'
                            },
                            overflow: 'hidden'
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                          >
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Chip 
                                  label={t(`ai.priority${insight.priority.charAt(0).toUpperCase() + insight.priority.slice(1)}`)}
                                  size="small"
                                  color={
                                    insight.priority === 'high' ? 'error' : 
                                    insight.priority === 'medium' ? 'warning' : 'info'
                                  }
                                  sx={{ mr: 1 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(insight.created_at).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {insight.title}
                              </Typography>
                              <Chip 
                                label={
                                  CONVERSATION_TOPICS.find(t => t.value === insight.topic)?.label || 
                                  insight.topic
                                }
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Box 
                              dangerouslySetInnerHTML={{ __html: insight.formatted_response }}
                              sx={{
                                '& ul': {
                                  paddingLeft: '20px',
                                  marginTop: '8px',
                                  marginBottom: '8px'
                                },
                                '& li': {
                                  marginBottom: '4px'
                                }
                              }}
                            />
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </MotionDiv>
          </Grid>
        </Grid>
      </form>
      
      {/* AI Dialog */}
      <AiDialog
        open={openAiDialog}
        onClose={() => setOpenAiDialog(false)}
        currentAiResponse={currentAiResponse}
        isLoading={isGeneratingInsight}
        handleSaveInsight={handleSaveInsight}
        priority={query.priority}
      />
    </Box>
  );
};

export default AiInsight;